/**
 * The ends of a band: where it can be pulled longer or shorter along the plan.
 *
 * A band's border is a closed path — two long sides and, at each end, a short
 * straight edge across them where the border turns back on itself. Each such
 * edge gets a tab. Dragging the tab slides the end along the lines the sides
 * follow (the plan's rails), keeps the band exactly as wide as it is, and never
 * asks for a vertex to be moved by hand. A merged mark has a tab at every end.
 */
import type { Shape, Vec } from '../../shared/doc.ts';
import type { IntentEngine } from '../vision/engine.ts';
import { LiveStroke } from '../vision/livestroke.ts';
import { projectOnRail, railSlice, type Rail } from '../vision/rails.ts';
import { flattenPath } from './curve.ts';
import { offsetSide, shapeToPolygon } from './outline.ts';

export interface BandEnd {
  ia: number; // polygon index of the corner the border arrives at
  ib: number; // polygon index of the corner it leaves from
  a: Vec;
  b: Vec;
  mid: Vec;
  along: Vec; // unit, a → b
  out: Vec; // unit, away from the band along its length
  width: number;
}

export interface Border {
  poly: number[];
  anchors: number[] | null; // polygon indices of a path's anchors; null for a polygon (every vertex counts)
}

/** How an end travels when its tab is dragged. */
export type EndGuide =
  | { kind: 'rails'; a: number[]; b: number[]; exact?: boolean } // each side's line from its corner outward (a polyline starting at the corner); exact: the sides already end where the end is to be cut
  | { kind: 'line'; c: number[]; half: number } // a band on one line: that line from the end's centre outward, and half the band width
  | { kind: 'straight' }; // nothing to follow: the sides carry straight on

const COLLINEAR = Math.cos((1.5 * Math.PI) / 180);
const ALIGN = Math.cos((35 * Math.PI) / 180); // a line follows a side when their directions agree within this
const TURN_AWAY = 0.34; // a side that has turned more than ~70° from the band's direction is no longer "along" it

const v = (x: number, y: number): Vec => ({ x, y });
const add = (a: Vec, b: Vec): Vec => v(a.x + b.x, a.y + b.y);
const sub = (a: Vec, b: Vec): Vec => v(a.x - b.x, a.y - b.y);
const mul = (a: Vec, k: number): Vec => v(a.x * k, a.y * k);
const dot = (a: Vec, b: Vec): number => a.x * b.x + a.y * b.y;
const len = (a: Vec): number => Math.hypot(a.x, a.y);
const dist = (a: Vec, b: Vec): number => Math.hypot(a.x - b.x, a.y - b.y);
const unit = (a: Vec): Vec => {
  const l = len(a) || 1;
  return v(a.x / l, a.y / l);
};
const mod = (i: number, n: number): number => ((i % n) + n) % n;

/** The flattened border of a shape (paths keep track of which vertices are anchors). */
export function borderOf(s: Shape): Border | null {
  if (s.k === 'path') {
    const anchors: number[] = [];
    return { poly: flattenPath(s.pts, s.h, 4, anchors), anchors };
  }
  if (s.k === 'poly') return { poly: s.pts, anchors: null };
  // a legacy band or rectangle: its outline, every vertex a corner
  const poly = shapeToPolygon(s);
  return poly.length >= 6 ? { poly, anchors: null } : null;
}

/**
 * The band's ends: straight edges between `minLen` and `maxLen` long (and short
 * against the whole border) at which the border reverses direction — the sides
 * arrive at one corner and leave the other heading back the way they came,
 * both roughly square to the edge. A step where a band widens continues in the
 * same direction and is not an end; a rectangle's long sides are not ends.
 * `seam` says whether an edge lies along a neighbouring mark (the cut where one
 * placement abuts another): such an edge is an end however the band widens
 * right behind it.
 */
export function bandEnds(border: Border, minLen: number, maxLen: number, reach: number, seam: ((a: Vec, b: Vec) => boolean) | null = null): BandEnd[] {
  const { poly } = border;
  const n = poly.length / 2;
  if (n < 4) return [];
  const P = (i: number): Vec => v(poly[2 * mod(i, n)]!, poly[2 * mod(i, n) + 1]!);
  const anchorSet = border.anchors ? new Set(border.anchors) : null;
  const perim = polyLen(poly, true);
  const out: BandEnd[] = [];
  const used = new Set<number>();
  for (let start = 0; start < n; start++) {
    if (used.has(start) || (anchorSet && !anchorSet.has(start))) continue;
    // a straight edge: a path's consecutive anchors (no samples between them), extended while collinear
    let end = start;
    const d0 = unit(sub(P(start + 1), P(start)));
    for (let steps = 0; steps < n - 1; steps++) {
      const nx = end + 1;
      if (anchorSet && !anchorSet.has(mod(nx, n))) break;
      if (dot(unit(sub(P(nx), P(end))), d0) < COLLINEAR) break;
      end = nx;
    }
    if (end === start) continue;
    for (let k = start + 1; k < end; k++) used.add(mod(k, n)); // a run's interior vertices start no run of their own
    const a = P(start),
      b = P(end);
    const L = dist(a, b);
    if (L < minLen || L > maxLen || L > 0.22 * perim) continue;
    const r = Math.max(reach, 0.75 * L);
    // the sides' directions are read up to the next corner: a short stub beside a merge (a branch
    // pulled back to where it joins) must not have its side read across the merge's apex
    const along = unit(sub(b, a));
    // a side's direction is that of its stretches running along the band; a short edge running
    // across it (the step of a widening just behind the end) does not tilt the reading
    const arr = sideDir(poly, start, -1, r, along, unit(sub(a, walkStraight(poly, start, -1, r)))),
      dep = sideDir(poly, end, 1, r, along, unit(sub(walkStraight(poly, end, 1, r), b)));
    if (dot(arr, dep) > -0.5) continue;
    if (Math.abs(dot(arr, along)) > 0.6 || Math.abs(dot(dep, along)) > 0.6) continue;
    if (!seam?.(a, b)) {
      // a band runs on behind its end on both sides (three quarters of its width at least, or up
      // to the nook where a stub joins another band): the short edge at the foot of a widening
      // step, or a wide shape's long side, is not an end
      const da = bandDepth(poly, start, -1, L),
        db = bandDepth(poly, end, 1, L);
      if (!(da.nook || da.d >= 0.75 * L) || !(db.nook || db.d >= 0.75 * L)) continue;
      // … and two band widths back (or as far as the shorter side goes before a nook) the sides
      // are still about a band width apart: behind the foot of a widening step they run apart
      const r2 = Math.max(reach, 2 * L);
      const d2 = Math.min(walkStraightD(poly, start, -1, r2).d, walkStraightD(poly, end, 1, r2).d);
      const apart = dist(walkStraight(poly, start, -1, d2), walkStraight(poly, end, 1, d2));
      if (apart > 1.6 * L || apart < 0.4 * L) continue;
    }
    let o = sub(arr, dep);
    o = sub(o, mul(along, dot(o, along)));
    const ol = len(o);
    if (ol < 1e-6) continue;
    out.push({ ia: start, ib: mod(end, n), a, b, mid: v((a.x + b.x) / 2, (a.y + b.y) / 2), along, out: mul(o, 1 / ol), width: L });
  }
  // two ends never share a corner: the sliver of trench between a widening step and the band's
  // real end reads as a tiny end of its own, with the real end's edge for a side — the wider wins
  return out.filter((e) => !out.some((f) => f !== e && (f.ia === e.ib || f.ib === e.ia) && f.width > e.width));
}

/**
 * A tab being dragged. The plan is read the way a stroke is: the tracer is
 * started along the band's own midline just inside the end, then fed the tab's
 * positions, so the pulled end follows exactly what a finger drawing onward
 * would get — the corridor's lines, a widening onto the next edge, a line
 * followed through junctions — and, like a finger, the tab is where the band
 * ends: the tracer's sides reach exactly as far as the tab and come back with
 * it (a widening the tab backs out of is undone). Inside the original band the
 * border is cut back along its own sides. Without a reading, the end follows
 * the lines through its corners, or carries straight on.
 */
export interface EndPull {
  border: Border;
  end: BandEnd;
  fallback: EndGuide;
  live: LiveStroke | null;
  minKeep: number;
  /** The end edge as last pulled (the tab is drawn across it while the pull lasts). */
  current: { a: Vec; b: Vec } | null;
  /** The plan's size: an end is never pulled past its edge. */
  bounds: { w: number; h: number } | null;
}

/** A pulled border with its new end edge. */
export interface Pulled {
  poly: number[];
  a: Vec;
  b: Vec;
}

export function beginPull(border: Border, end: BandEnd, engine: IntentEngine | null, tol: number, u: number, maxLen: number, bounds: { w: number; h: number } | null = null): EndPull {
  const fallback = endGuide(end, engine, Math.max(3, 1.5 * u), maxLen);
  let live: LiveStroke | null = null;
  if (engine) {
    const seed = seedRoute(border, end, 8 * tol, tol);
    if (seed.length >= 4) {
      live = new LiveStroke(engine, tol, { width: end.width, margin: 0 });
      const pts: Vec[] = [];
      for (let i = 0; i < seed.length; i += 2) pts.push(v(seed[i]!, seed[i + 1]!));
      live.add(pts);
      live.update();
      if (live.mode === 'free') live = null; // nothing read under the band itself
    }
  }
  // a pulled-back end keeps at least this much band: more than the fitter's corner-merging reach, so a stub keeps its square end and its tab
  return { border, end, fallback, live, minKeep: 8 * u, current: null, bounds };
}

/** The border with the end pulled to where the tab is now: out along the plan, or back along the band's own sides. */
export function pullTo(p: EndPull, tab: Vec): number[] {
  const { end } = p;
  const along = dot(sub(tab, end.mid), end.out);
  // the tracer sees every position, so its reach always agrees with the tab (back inside the band too)
  if (p.live) {
    p.live.add([tab]);
    p.live.update();
  }
  let r: Pulled | null = null;
  if (along < 0) r = retractEnd(p.border, end, -along, p.minKeep);
  else {
    const g = p.live ? tracedGuide(p.live, end) : null;
    if (g) r = extendEnd(p.border, end, g, tab, p.bounds);
    if (!r) r = extendEnd(p.border, end, p.fallback, tab, p.bounds);
  }
  if (!r) r = { poly: p.border.poly, a: end.a, b: end.b };
  p.current = { a: r.a, b: r.b };
  return r.poly;
}

/** The band's midline from `len` px inside the end out to the end, sampled about every step/3 px (the tracer's own anchor spacing). */
function seedRoute(border: Border, end: BandEnd, len: number, step: number): number[] {
  const { poly } = border;
  const n = poly.length / 2;
  const rot = rotateTo(poly, end.ib);
  const A = n - mod(end.ib - end.ia, n);
  if (A < 2) return [];
  const lim = Math.min(len, walkLimit(rot, 0, 1, A, end.out), walkLimit(rot, A, -1, A, end.out));
  if (lim < 2 * step) return [];
  const k = Math.max(3, Math.ceil(lim / Math.max(1, step / 3)));
  const out: number[] = [];
  for (let i = k; i >= 0; i--) {
    const s = (i / k) * lim;
    const pb = walk(rot, 0, 1, s).p,
      pa = walk(rot, A, -1, s).p;
    out.push((pa.x + pb.x) / 2, (pa.y + pb.y) / 2);
  }
  return out;
}

/** The sides the tracer has read beyond the end, each starting at its corner and ending at the tab (the band goes no further than the lines it follows). */
function tracedGuide(live: LiveStroke, end: BandEnd): EndGuide | null {
  const run = live.sideGroups()[0];
  if (!run) return null;
  const { left, right } = run;
  if (left.length < 4 || right.length < 4) return null;
  const leftIsA = projectOnPolyline(left, end.a).d <= projectOnPolyline(right, end.a).d;
  const [ga, gb] = leftIsA ? [left, right] : [right, left];
  const plan = live.engine.plan;
  return { kind: 'rails', a: fromCorner(ga, end.a, plan), b: fromCorner(gb, end.b, plan), exact: true };
}

/** The part of a side beyond a corner, starting exactly at the corner. */
function fromCorner(side: number[], corner: Vec, plan: { w: number; h: number } | null): number[] {
  const s = projectOnPolyline(side, corner).s;
  const rest = slicePolyline(side, s, polyLen(side, false));
  if (rest.length < 4) return [corner.x, corner.y];
  rest[0] = corner.x;
  rest[1] = corner.y;
  return plan ? toBorder(rest, plan.w, plan.h) : rest;
}

/**
 * A line that ends just short of the image's edge (the skeleton stops a few px in) is carried
 * straight on to the edge, so a band pulled all the way butts right up against it.
 */
export function toBorder(pts: number[], w: number, h: number, within = 8): number[] {
  const n = pts.length / 2;
  if (n < 2) return pts;
  const ex = pts[2 * n - 2]!,
    ey = pts[2 * n - 1]!;
  const d = dirAlong(pts, polyLen(pts, false));
  // how far along d to the nearest image edge in that direction
  let t = Infinity;
  if (d.x > 1e-6) t = Math.min(t, (w - ex) / d.x);
  if (d.x < -1e-6) t = Math.min(t, (0 - ex) / d.x);
  if (d.y > 1e-6) t = Math.min(t, (h - ey) / d.y);
  if (d.y < -1e-6) t = Math.min(t, (0 - ey) / d.y);
  if (!(t > 0.01) || t > within) return pts;
  return [...pts, ex + d.x * t, ey + d.y * t];
}

/**
 * What an end follows when dragged without a reading of the plan: the lines
 * through its two corners (a corridor), else a line through its centre (a band
 * on one line), else nothing (the sides carry straight on). Lines are followed
 * on past the end of a rail onto the rail that continues them.
 */
export function endGuide(end: BandEnd, engine: IntentEngine | null, tol: number, maxLen: number): EndGuide {
  if (engine) {
    const la = lineFrom(engine, end.a, end.out, tol, maxLen),
      lb = lineFrom(engine, end.b, end.out, tol, maxLen);
    if (la && lb) return { kind: 'rails', a: la, b: lb };
    const lc = lineFrom(engine, end.mid, end.out, Math.max(tol, 0.35 * end.width), maxLen);
    if (lc) return { kind: 'line', c: lc, half: end.width / 2 };
  }
  return { kind: 'straight' };
}

/** The line through p heading along `dir`, as a polyline starting at p, followed for up to maxLen px. */
function lineFrom(engine: IntentEngine, p: Vec, dir: Vec, maxOff: number, maxLen: number): number[] | null {
  let best: { rail: Rail; s: number; fwd: boolean; d: number } | null = null;
  for (const id of engine.segsNear(p, maxOff + 2)) {
    const rail = engine.rails.railOf(id);
    if (rail.loop) continue;
    const pr = projectOnRail(rail, p, maxOff);
    if (!pr || pr.over > 1) continue;
    const c = pr.tx * dir.x + pr.ty * dir.y;
    if (Math.abs(c) < ALIGN) continue;
    if (!best || pr.d < best.d) best = { rail, s: pr.s, fwd: c > 0, d: pr.d };
  }
  if (!best) return null;
  const pts = [p.x, p.y];
  const used = new Set<number>(best.rail.segs);
  let cur: { rail: Rail; s: number; fwd: boolean } = best;
  for (let hop = 0; hop < 6; hop++) {
    const slice = cur.fwd ? railSlice(cur.rail, cur.s, cur.rail.len) : railSlice(cur.rail, 0, cur.s);
    if (!cur.fwd) reverseFlat(slice);
    appendPts(pts, slice);
    if (polyLen(pts, false) >= maxLen) break;
    const next = continueLine(engine, cur.rail, cur.fwd, used);
    if (!next) break;
    cur = next;
  }
  return pts.length >= 4 ? toBorder(pts, engine.plan.w, engine.plan.h) : null;
}

/** The rail that carries a line on past the end of `rail` (a fragment ends at a junction the line runs through). */
function continueLine(engine: IntentEngine, rail: Rail, fwd: boolean, used: Set<number>): { rail: Rail; s: number; fwd: boolean } | null {
  const n = rail.pts.length / 2;
  if (n < 2) return null;
  const e = fwd ? v(rail.pts[2 * n - 2]!, rail.pts[2 * n - 1]!) : v(rail.pts[0]!, rail.pts[1]!);
  const q = fwd ? v(rail.pts[2 * n - 4]!, rail.pts[2 * n - 3]!) : v(rail.pts[2]!, rail.pts[3]!);
  const t = unit(sub(e, q));
  let best: { rail: Rail; s: number; fwd: boolean; remaining: number } | null = null;
  for (const id of engine.segsNear(e, 12)) {
    if (used.has(id)) continue;
    const r2 = engine.rails.railOf(id);
    if (r2 === rail || r2.loop || r2.segs.some((s) => used.has(s))) continue;
    if (r2.thick < 0.66 * rail.thick || rail.thick < 0.6 * r2.thick) continue; // a line of another weight is another line
    const pr = projectOnRail(r2, e, 8);
    if (!pr) continue;
    const c = pr.tx * t.x + pr.ty * t.y;
    if (Math.abs(c) < ALIGN) continue;
    const f2 = c > 0;
    const remaining = f2 ? r2.len - pr.s : pr.s;
    if (remaining < 6) continue;
    if (!best || remaining > best.remaining) best = { rail: r2, s: pr.s, fwd: f2, remaining };
  }
  if (best) for (const s of best.rail.segs) used.add(s);
  return best;
}

/**
 * The border with this end pulled out to the finger: the sides run on along
 * their guide to where the finger projects onto it (or, for an exact guide, as
 * far as the guide itself goes — the tracer has already cut it at the finger),
 * and the end is cut square across there. Null when the finger is not beyond
 * the end.
 */
export function extendEnd(border: Border, end: BandEnd, guide: EndGuide, finger: Vec, bounds: { w: number; h: number } | null = null): Pulled | null {
  let sideA: number[], sideB: number[]; // from each corner outward to the new corner
  if (guide.kind === 'rails' && guide.exact) {
    if (polyLen(guide.a, false) < 0.5 && polyLen(guide.b, false) < 0.5) return null;
    sideA = guide.a.slice();
    sideB = guide.b.slice();
  } else if (guide.kind === 'rails') {
    const pa = projectOnPolyline(guide.a, finger),
      pb = projectOnPolyline(guide.b, finger);
    if (pa.s < 0.5 && pb.s < 0.5) return null;
    let sa = Math.max(0.5, pa.s),
      sb = Math.max(0.5, pb.s);
    // the end stays square: when one side runs on past the other (its line went on where the
    // other's ended, or the tab sits beside one of them), it is cut abreast of the shorter one
    const A = pointAlong(guide.a, sa),
      B = pointAlong(guide.b, sb);
    const dir = unit(add(dirAlong(guide.a, sa), dirAlong(guide.b, sb)));
    const e = sub(B, A);
    const el = len(e);
    if (el > 1e-6 && Math.abs(dot(e, dir)) / el > 0.5) {
      if (dot(sub(A, B), dir) > 0) sa = Math.max(0.5, projectOnPolyline(guide.a, B).s);
      else sb = Math.max(0.5, projectOnPolyline(guide.b, A).s);
    }
    sideA = slicePolyline(guide.a, 0, sa);
    sideB = slicePolyline(guide.b, 0, sb);
  } else if (guide.kind === 'line') {
    const pc = projectOnPolyline(guide.c, finger);
    if (pc.s < 0.5) return null;
    const c = slicePolyline(guide.c, 0, pc.s);
    const l = offsetSide(c, guide.half),
      r = offsetSide(c, -guide.half);
    const lIsA = dist(v(l[0]!, l[1]!), end.a) <= dist(v(r[0]!, r[1]!), end.a);
    sideA = lIsA ? l : r;
    sideB = lIsA ? r : l;
    sideA[0] = end.a.x;
    sideA[1] = end.a.y;
    sideB[0] = end.b.x;
    sideB[1] = end.b.y;
  } else {
    const d = dot(sub(finger, end.mid), end.out);
    if (d < 0.5) return null;
    sideA = [end.a.x, end.a.y, end.a.x + end.out.x * d, end.a.y + end.out.y * d];
    sideB = [end.b.x, end.b.y, end.b.x + end.out.x * d, end.b.y + end.out.y * d];
  }
  if (bounds) {
    // never past the plan's edge: each side stops where it meets the image border
    sideA = clipToRect(sideA, bounds.w, bounds.h);
    sideB = clipToRect(sideB, bounds.w, bounds.h);
    if (sideA.length < 4 && sideB.length < 4) return null;
    if (sideA.length < 4) sideA = [end.a.x, end.a.y, end.a.x, end.a.y];
    if (sideB.length < 4) sideB = [end.b.x, end.b.y, end.b.x, end.b.y];
  }
  const { poly } = border;
  const n = poly.length / 2;
  const rot = rotateTo(poly, end.ib); // starts at b; a is at index A, the edge's other vertices after it
  const A = n - mod(end.ib - end.ia, n);
  const out = rot.slice(0, 2 * (A + 1));
  appendPts(out, sideA.slice(2));
  const back = sideB.slice();
  reverseFlat(back);
  appendPts(out, back.slice(0, -2));
  return { poly: out, a: v(sideA[sideA.length - 2]!, sideA[sideA.length - 1]!), b: v(sideB[sideB.length - 2]!, sideB[sideB.length - 1]!) };
}

/**
 * The border with this end pulled back by `amount` px along the band: both
 * sides are cut that far in and the end closed square across. The band keeps
 * at least `minKeep` px of length, and a side is never followed round a bend
 * (a step where the band widens is not a bend: the cut passes it whole).
 */
export function retractEnd(border: Border, end: BandEnd, amount: number, minKeep: number): Pulled | null {
  const { poly } = border;
  const n = poly.length / 2;
  const rot = rotateTo(poly, end.ib);
  const A = n - mod(end.ib - end.ia, n);
  if (A < 2) return null;
  const max = Math.min(walkLimit(rot, 0, 1, A, end.out, end.width), walkLimit(rot, A, -1, A, end.out, end.width)) - minKeep;
  const d = Math.min(amount, max);
  if (d < 0.5) return null;
  let wb = walk(rot, 0, 1, d),
    wa = walk(rot, A, -1, d);
  const inward = mul(end.out, -1);
  // a cut never lands on an edge running across the band (the step of a widening): the whole
  // edge goes, else the closing edge would run along the polygon's own side as a spike of no width
  const P = (j: number): Vec => v(rot[2 * mod(j, n)]!, rot[2 * mod(j, n) + 1]!);
  const across = (i: number, j: number): boolean => {
    const u = sub(P(j), P(i));
    const l = len(u);
    return l > 1e-6 && Math.abs(dot(u, inward)) / l < 0.5;
  };
  if (wb.k + 1 <= A && across(wb.k, wb.k + 1)) wb = { p: P(wb.k + 1), k: wb.k + 1 };
  if (wa.k - 1 >= 0 && across(wa.k, wa.k - 1)) wa = { p: P(wa.k - 1), k: wa.k - 1 };
  // the end stays square: where one side ran further in than the other (a branch pulled back to
  // its merge, a bend), the side that is ahead is cut abreast of the other
  const e = sub(wa.p, wb.p);
  const el = len(e);
  if (el > 1e-6 && Math.abs(dot(e, inward)) / el > 0.17) {
    if (dot(sub(wa.p, wb.p), inward) > 0) {
      // A is further in: the point of A's walked stretch nearest B's point (vertices wa.k − 1 … A)
      const q = nearestOnRange(rot, Math.max(0, wa.k - 1), A, wb.p);
      wa = { p: q.p, k: q.k + 1 };
    } else {
      const q = nearestOnRange(rot, 0, Math.min(A, wb.k + 1), wa.p);
      wb = { p: q.p, k: q.k };
    }
  }
  const out = [wb.p.x, wb.p.y];
  for (let k = wb.k + 1; k <= wa.k - 1; k++) out.push(rot[2 * k]!, rot[2 * k + 1]!);
  out.push(wa.p.x, wa.p.y);
  return out.length >= 6 ? { poly: out, a: wa.p, b: wb.p } : null;
}

/** The nearest point to p on the polyline's segments between vertex indices i0 and i1 (on segment k → k+1). */
function nearestOnRange(rot: number[], i0: number, i1: number, p: Vec): { p: Vec; k: number } {
  let best = { p: v(rot[2 * i0]!, rot[2 * i0 + 1]!), k: i0 },
    bd = Infinity;
  for (let k = i0; k < i1; k++) {
    const ax = rot[2 * k]!,
      ay = rot[2 * k + 1]!,
      bx = rot[2 * k + 2]!,
      by = rot[2 * k + 3]!;
    const dx = bx - ax,
      dy = by - ay;
    const l2 = dx * dx + dy * dy;
    const t = l2 ? Math.max(0, Math.min(1, ((p.x - ax) * dx + (p.y - ay) * dy) / l2)) : 0;
    const q = v(ax + dx * t, ay + dy * t);
    const dd = dist(q, p);
    if (dd < bd) {
      bd = dd;
      best = { p: q, k };
    }
  }
  return best;
}

/* ------------------------------------------------------------------ walking */

/** The point `d` px along the polygon from vertex i in direction dir, stopping short of a turn that doubles back (120° or more: a nook's apex). */
function walkStraight(poly: number[], i: number, dir: 1 | -1, d: number): Vec {
  return walkStraightD(poly, i, dir, d).p;
}
/**
 * The direction the border runs in over `d` px from vertex i (direction dir), taken from the
 * stretches that do not run across the band (within ~45° of `along`, the end edge's direction)
 * and weighted by length, pointing away from vertex i; `fallback` when nothing qualifies.
 */
function sideDir(poly: number[], i: number, dir: 1 | -1, d: number, along: Vec, fallback: Vec): Vec {
  const n = poly.length / 2;
  const P = (j: number): Vec => v(poly[2 * mod(j, n)]!, poly[2 * mod(j, n) + 1]!);
  let acc = 0,
    k = i,
    first: Vec | null = null,
    sx = 0,
    sy = 0;
  for (let steps = 0; steps < n && acc < d; steps++) {
    const a = P(k),
      b = P(k + dir);
    const l = dist(a, b);
    if (l > 1e-6) {
      const u = unit(sub(b, a));
      if (!first) first = u;
      else if (dot(u, first) < -0.5) break;
      const take = Math.min(l, d - acc);
      if (Math.abs(dot(u, along)) <= 0.7) {
        sx += u.x * take;
        sy += u.y * take;
      }
    }
    acc += l;
    k += dir;
  }
  const sl = Math.hypot(sx, sy);
  // both directions read along the border's own order (+1): the arriving side's walk ran backwards
  return sl < 1e-6 ? fallback : v((dir * sx) / sl, (dir * sy) / sl);
}
/**
 * How far a band runs on behind one corner of an end: the border followed from vertex i
 * (direction dir) while it keeps within 45° of its first stretch — through the step of a
 * widening (a stretch across the band no longer than `L` that the side resumes after) — until
 * it turns away for good. `nook`: the walk ended where the border doubles back (the apex of a
 * nook between two bands), so the side belongs to another band from there.
 */
function bandDepth(poly: number[], i: number, dir: 1 | -1, L: number): { d: number; nook: boolean } {
  const n = poly.length / 2;
  const P = (j: number): Vec => v(poly[2 * mod(j, n)]!, poly[2 * mod(j, n) + 1]!);
  let acc = 0,
    k = i,
    u0: Vec | null = null;
  for (let steps = 0; steps < n; steps++) {
    const a = P(k),
      b = P(k + dir);
    const l = dist(a, b);
    if (l > 1e-6) {
      const u = unit(sub(b, a));
      if (!u0) u0 = u;
      else if (dot(u, u0) < -0.5) return { d: acc, nook: true };
      else if (dot(u, u0) < 0.7) {
        // across the band: a step when short and the side resumes right after it
        const c = P(k + 2 * dir);
        const l2 = dist(b, c);
        const resumes = l <= L && l2 > 1e-6 && dot(unit(sub(c, b)), u0) >= 0.7;
        if (!resumes) return { d: acc, nook: false };
        k += dir;
        continue;
      }
      acc += l;
    }
    k += dir;
  }
  return { d: acc, nook: false };
}
/** walkStraight with the distance actually covered (less than d when the walk stopped at a doubling-back turn). */
function walkStraightD(poly: number[], i: number, dir: 1 | -1, d: number): { p: Vec; d: number } {
  const n = poly.length / 2;
  const P = (j: number): Vec => v(poly[2 * mod(j, n)]!, poly[2 * mod(j, n) + 1]!);
  let acc = 0,
    k = i,
    first: Vec | null = null;
  for (let steps = 0; steps < n; steps++) {
    const a = P(k),
      b = P(k + dir);
    const l = dist(a, b);
    if (l > 1e-6) {
      const u = unit(sub(b, a));
      if (!first) first = u;
      else if (dot(u, first) < -0.5) return { p: a, d: acc };
    }
    if (acc + l >= d) {
      const t = l ? (d - acc) / l : 0;
      return { p: v(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t), d };
    }
    acc += l;
    k += dir;
  }
  return { p: P(k), d: acc };
}
/** The point `d` px along the polygon from vertex i in direction dir (wrapping); k = the last vertex passed. */
function walk(poly: number[], i: number, dir: 1 | -1, d: number): { p: Vec; k: number } {
  const n = poly.length / 2;
  const P = (j: number): Vec => v(poly[2 * mod(j, n)]!, poly[2 * mod(j, n) + 1]!);
  let acc = 0,
    k = i;
  for (let steps = 0; steps < n; steps++) {
    const a = P(k),
      b = P(k + dir);
    const l = dist(a, b);
    if (acc + l >= d) {
      const t = l ? (d - acc) / l : 0;
      return { p: v(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t), k: mod(k, n) };
    }
    acc += l;
    k += dir;
  }
  return { p: P(k), k: mod(k, n) };
}

/**
 * How far a side can be followed from vertex i (direction dir) before it turns away from
 * the band's direction (−out) or reaches the far corner at index `limitIdx` (0 or A of a
 * rotated polygon), so an end is never pulled round a bend or past the other corner. With
 * `pass` given, a turn the side comes back from within about a band width (the step where
 * a band widens) is not a bend: the side is followed through it.
 */
function walkLimit(rot: number[], i: number, dir: 1 | -1, A: number, out: Vec, pass = 0): number {
  const n = rot.length / 2;
  const P = (j: number): Vec => v(rot[2 * j]!, rot[2 * j + 1]!);
  const inward = mul(out, -1);
  const stopIdx = dir > 0 ? A : 0;
  let acc = 0;
  const trail: { s: number; p: Vec }[] = [{ s: 0, p: P(i) }];
  const resumes = (k: number): boolean => {
    // the side is heading the band's way again within `pass` px of border from vertex k
    let s = 0;
    for (let j = k; j !== stopIdx && j + dir >= 0 && j + dir < n && s < 2 * pass; j += dir) {
      const a = P(j),
        b = P(j + dir);
      const l = dist(a, b);
      if (l > 1e-6 && dot(unit(sub(b, a)), inward) >= 0.7) return true;
      s += l;
    }
    return false;
  };
  for (let k = i; k !== stopIdx && k + dir >= 0 && k + dir < n; k += dir) {
    const a = P(k),
      b = P(k + dir);
    const l = dist(a, b);
    acc += l;
    trail.push({ s: acc, p: b });
    // the direction over the last 12 px of border
    let back = trail[0]!;
    for (let j = trail.length - 1; j >= 0; j--) if (acc - trail[j]!.s >= 12) {
      back = trail[j]!;
      break;
    }
    if (acc - back.s < 3) continue;
    if (dot(unit(sub(b, back.p)), inward) < TURN_AWAY && !(pass > 0 && resumes(k + dir))) return Math.max(0, back.s);
    if (k + dir === stopIdx) break;
  }
  return acc;
}

/* ----------------------------------------------------------------- helpers */

function rotateTo(poly: number[], i: number): number[] {
  const n = poly.length / 2;
  const s = mod(i, n);
  return [...poly.slice(2 * s), ...poly.slice(0, 2 * s)];
}
function reverseFlat(pts: number[]): void {
  for (let i = 0, j = pts.length - 2; i < j; i += 2, j -= 2) {
    const x = pts[i]!,
      y = pts[i + 1]!;
    pts[i] = pts[j]!;
    pts[i + 1] = pts[j + 1]!;
    pts[j] = x;
    pts[j + 1] = y;
  }
}
function appendPts(dst: number[], src: number[]): void {
  for (let i = 0; i < src.length; i += 2) {
    const m = dst.length;
    if (m === 0 || Math.hypot(src[i]! - dst[m - 2]!, src[i + 1]! - dst[m - 1]!) > 0.2) dst.push(src[i]!, src[i + 1]!);
  }
}
function polyLen(pts: number[], closed: boolean): number {
  let l = 0;
  for (let i = 2; i < pts.length; i += 2) l += Math.hypot(pts[i]! - pts[i - 2]!, pts[i + 1]! - pts[i - 1]!);
  if (closed && pts.length >= 4) l += Math.hypot(pts[0]! - pts[pts.length - 2]!, pts[1]! - pts[pts.length - 1]!);
  return l;
}
/** Nearest point of an open polyline to p: its arc length from the start. */
function projectOnPolyline(pts: number[], p: Vec): { s: number; d: number } {
  let best = Infinity,
    bs = 0,
    acc = 0;
  for (let i = 0; i + 3 < pts.length; i += 2) {
    const ax = pts[i]!,
      ay = pts[i + 1]!,
      bx = pts[i + 2]!,
      by = pts[i + 3]!;
    const dx = bx - ax,
      dy = by - ay;
    const l2 = dx * dx + dy * dy;
    const t = l2 ? Math.max(0, Math.min(1, ((p.x - ax) * dx + (p.y - ay) * dy) / l2)) : 0;
    const d = Math.hypot(p.x - (ax + dx * t), p.y - (ay + dy * t));
    if (d < best) {
      best = d;
      bs = acc + t * Math.sqrt(l2);
    }
    acc += Math.sqrt(l2);
  }
  return { s: bs, d: best };
}
/** An open polyline cut where it first leaves the rectangle [0,w]×[0,h] (the cut point on the border kept). */
export function clipToRect(pts: number[], w: number, h: number): number[] {
  const inside = (x: number, y: number) => x >= 0 && x <= w && y >= 0 && y <= h;
  if (pts.length < 2 || !inside(pts[0]!, pts[1]!)) return pts.length >= 2 && !inside(pts[0]!, pts[1]!) ? [] : pts;
  const out = [pts[0]!, pts[1]!];
  for (let i = 2; i < pts.length; i += 2) {
    const ax = pts[i - 2]!,
      ay = pts[i - 1]!,
      bx = pts[i]!,
      by = pts[i + 1]!;
    if (inside(bx, by)) {
      out.push(bx, by);
      continue;
    }
    // the fraction of the segment still inside
    let t = 1;
    if (bx < 0) t = Math.min(t, (0 - ax) / (bx - ax));
    if (bx > w) t = Math.min(t, (w - ax) / (bx - ax));
    if (by < 0) t = Math.min(t, (0 - ay) / (by - ay));
    if (by > h) t = Math.min(t, (h - ay) / (by - ay));
    t = Math.max(0, Math.min(1, t));
    if (t > 1e-6) out.push(ax + (bx - ax) * t, ay + (by - ay) * t);
    return out;
  }
  return out;
}
/** The point of an open polyline at arc length s (clamped). */
function pointAlong(pts: number[], s: number): Vec {
  let acc = 0;
  for (let i = 0; i + 3 < pts.length; i += 2) {
    const l = Math.hypot(pts[i + 2]! - pts[i]!, pts[i + 3]! - pts[i + 1]!);
    if (acc + l >= s) {
      const t = l ? Math.max(0, (s - acc) / l) : 0;
      return v(pts[i]! + (pts[i + 2]! - pts[i]!) * t, pts[i + 1]! + (pts[i + 3]! - pts[i + 1]!) * t);
    }
    acc += l;
  }
  return v(pts[pts.length - 2]!, pts[pts.length - 1]!);
}
/** The direction of an open polyline at arc length s (of the segment there, or the last one). */
function dirAlong(pts: number[], s: number): Vec {
  let acc = 0;
  for (let i = 0; i + 3 < pts.length; i += 2) {
    const l = Math.hypot(pts[i + 2]! - pts[i]!, pts[i + 3]! - pts[i + 1]!);
    if (acc + l >= s && l > 1e-6) return v((pts[i + 2]! - pts[i]!) / l, (pts[i + 3]! - pts[i + 1]!) / l);
    acc += l;
  }
  const n = pts.length;
  return n >= 4 ? unit(v(pts[n - 2]! - pts[n - 4]!, pts[n - 1]! - pts[n - 3]!)) : v(1, 0);
}
/** The part of an open polyline between arc lengths s0 and s1 (ends interpolated). */
function slicePolyline(pts: number[], s0: number, s1: number): number[] {
  const out: number[] = [];
  let acc = 0;
  for (let i = 0; i + 3 < pts.length; i += 2) {
    const ax = pts[i]!,
      ay = pts[i + 1]!,
      bx = pts[i + 2]!,
      by = pts[i + 3]!;
    const l = Math.hypot(bx - ax, by - ay);
    if (acc + l >= s0 && out.length === 0) {
      const t = l ? Math.max(0, (s0 - acc) / l) : 0;
      out.push(ax + (bx - ax) * t, ay + (by - ay) * t);
    }
    if (acc + l >= s1) {
      const t = l ? Math.max(0, Math.min(1, (s1 - acc) / l)) : 0;
      out.push(ax + (bx - ax) * t, ay + (by - ay) * t);
      return out;
    }
    if (out.length) out.push(bx, by);
    acc += l;
  }
  if (out.length === 0 && pts.length >= 2) out.push(pts[0]!, pts[1]!);
  return out;
}
